import type { CSSProperties } from "react";

/**
 * ÇARK TEMASI — "Klasik TV" Çarkıfelek çiziminin (`cark-klasik-tv.dc.html`, Emre 07.10.2026) renkleri BİREBİR.
 *
 * NEDEN PROJE BELİRTEÇLERİ DEĞİL: çark, uygulamanın geri kalanından bilerek ayrılan tek "sahne"dir (koyu mor zemin,
 * altın çerçeve, TV yarışması renkleri). Bu renklerin hiçbiri `tokens.css` paletinde yok ve palete eklemek onları
 * BÜTÜN ekranlarda kullanılabilir kılar — marka dışı bir mor/altın her yere sızardı. O yüzden ham renk YALNIZ bu
 * dosyada durur; `Cark.tsx` ve `cark.css` renkleri buradan (CSS değişkeni olarak) alır, kendisi renk yazmaz.
 *
 * Örtüşme denetlendi: `#FFFFFF` dışında hiçbir değer palette yok (`notr-0` beyaz ama çark yazısı tasarımda
 * `#FFFFFF` olarak geçiyor ve sahnenin kendi değişkeni; tek renk için iki kaynak açmamak adına burada tutuldu).
 */
export const CARK_TEMA = {
  zemin: "#160C2E",
  yazi: "#FFFFFF",
  kart: "#251646",
  cerceve: "#E9B23A",
  cerceveIc: "#6B3F07",
  ayrac: "#FFF3D6",
  mandal: "#FFF3D6",
  gobek: "#160C2E",
  gobekIc: "#E9B23A",
  dil: "#E53935",
  dilKenar: "#FFF3D6",
  vurgu: "#FFD54A",
  dugmeRenk: "#FFD54A",
  dugmeYazi: "#2A1500",
  dugmeGolge: "#B98A0F",
  kutlamaZemin: "#251646",
  kutlamaYildiz: "#2A1500",
  /** Normal dilimler bu sırayla boyanır (özel puanlar sırayı ilerletmez — tasarımdaki `ci++`). */
  renkler: ["#D32F2F", "#1E88E5", "#F9A825", "#2E7D32", "#8E24AA", "#EF6C00", "#00838F", "#C2185B"],
  ozel: { 500: "#111111", 1000: "#B0BEC5", 5000: "#FFD54A" } as Readonly<Record<number, string>>,
  ozelYazi: { 500: "#FFD54A", 1000: "#14213D", 5000: "#3B2000" } as Readonly<
    Record<number, string>
  >,
  ozelKontur: { 500: "#000000", 1000: "#ECEFF1", 5000: "#FFF3C4" } as Readonly<
    Record<number, string>
  >,
  normalYazi: "#FFFFFF",
  kontur: "#00000066",

  // Çizimin işaretleme kısmında doğrudan yazılı renkler (tema fonksiyonunun dışında).
  halka: "#FFD54A",
  icCizgi: "#FFF3C4",
  duy: "#8A5A12",
  lambaHale: "#FFE08A",
  lambaCekirdek: "#FFFBEA",
  dilParlak: "#FFFFFF",
  parlakBeyaz: "#FFFFFF",
  parlakSiyah: "#000000",
  perde: "rgba(8, 4, 20, 0.62)",

  /**
   * NABIZ: tasarımda `box-shadow: 0 0 0 0 rgba(255,213,74,0.55)` → `0 0 0 16px rgba(255,213,74,0)`. Gölge
   * animasyonu her karede yeniden boyama ister (Emre "takılıyor" dedi, performans kararı 8); aynı renk ayrı bir
   * katmanda `transform: scale` + `opacity` ile veriliyor.
   */
  nabiz: "rgba(255, 213, 74, 0.55)",
  /**
   * ÜZERİNE GELME: tasarımda `.cevir:hover { filter: brightness(1.07) }`. `filter` yerine üst katman `opacity`si
   * kullanılıyor; renkler brightness(1.07)'nin sRGB'de AYNI çarpımı: #FFD54A → #FFE44F, #B98A0F → #C69410
   * (255·1,07→255, 213·1,07=227,9→228, 74·1,07=79,2→79; 185→198, 138→148, 15→16).
   */
  dugmeParlak: "#FFE44F",
  dugmeGolgeParlak: "#C69410",
} as const;

/**
 * Yazı tipleri. Tasarım başlık ve rakamlarda Bricolage Grotesque (600/800) istiyor; proje fontları `@fontsource`
 * ile KENDİ İÇİNDE barındırıyor (`stil.css`: Google Fonts'a istek = kullanıcının IP'si üçüncü tarafa) ve Bricolage
 * paketi henüz bağımlılıklarda yok. Yığının başındaki ad `@fontsource-variable/bricolage-grotesque` paketinin
 * kaydettiği aile adıdır: paket eklenip `stil.css`e tek `@import` yazıldığı gün kod değişmeden devreye girer.
 * O güne kadar Inter Variable 800 kullanılır (TASARIMDAN SAPMA — teslim raporunda sayıyla).
 *
 * Yerel kurulu "Bricolage Grotesque" BİLEREK yığında değil: bazı cihazlarda farklı çizilmesin.
 */
export const CARK_BASLIK_FONTU =
  "'Bricolage Grotesque Variable', 'Inter Variable', Inter, system-ui, sans-serif";
export const CARK_GOVDE_FONTU = "'Inter Variable', Inter, system-ui, sans-serif";

/** `cark.css` bu değişkenleri okur; sahnenin kök öğesine bir kez yazılır. */
export const CARK_CSS_DEGISKENLERI = {
  "--cark-zemin": CARK_TEMA.zemin,
  "--cark-yazi": CARK_TEMA.yazi,
  "--cark-kart": CARK_TEMA.kart,
  "--cark-cerceve": CARK_TEMA.cerceve,
  "--cark-vurgu": CARK_TEMA.vurgu,
  "--cark-dugme-renk": CARK_TEMA.dugmeRenk,
  "--cark-dugme-yazi": CARK_TEMA.dugmeYazi,
  "--cark-dugme-golge": CARK_TEMA.dugmeGolge,
  "--cark-dugme-parlak": CARK_TEMA.dugmeParlak,
  "--cark-dugme-golge-parlak": CARK_TEMA.dugmeGolgeParlak,
  "--cark-kutlama-zemin": CARK_TEMA.kutlamaZemin,
  "--cark-perde": CARK_TEMA.perde,
  "--cark-nabiz": CARK_TEMA.nabiz,
  "--cark-baslik-fontu": CARK_BASLIK_FONTU,
  "--cark-govde-fontu": CARK_GOVDE_FONTU,
} as CSSProperties;
